import { useCallback, useEffect, useRef, useState } from 'react';
import { getCustomerDaySummary, getCustomerKpiSummary } from '../infra/customerRepository';
import { subscribeCustomerSynced } from '../../sync/core/customerSyncListeners';
import type { CustomerDaySummary, CustomerKpiSummary } from '../domain/customerTypes';

export type CustomerKpis = {
  kpiSummary: CustomerKpiSummary | null;
  daySummary: CustomerDaySummary | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

const EMPTY_KPI: CustomerKpiSummary = { debtTotal: 0, debtCount: 0, overdueCount: 0, blockedCount: 0 };
const EMPTY_DAY: CustomerDaySummary = { paymentsCount: 0, paymentsTotal: 0, chargesCount: 0, chargesTotal: 0 };

/**
 * KPIs de customer — independientes de los filtros del listado.
 *
 * - Deuda / vencidos / bloqueados: portfolio de la **organización** (clientes no son por sucursal).
 * - Cobrado hoy / cargado hoy: movimientos de la **sucursal** actual.
 *
 * Se cargan al montar y cuando cambia la sucursal. Se refrescan:
 * - Tras eventos de sync (`subscribeCustomerSynced`)
 * - Manualmente vía `refresh()` tras mutaciones locales (cobro, cargo, bloqueo, etc.)
 */
export function useCustomerKpis(branchId: string | null | undefined): CustomerKpis {
  const [kpiSummary, setKpiSummary] = useState<CustomerKpiSummary | null>(null);
  const [daySummary, setDaySummary] = useState<CustomerDaySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const branchRef = useRef<string | null | undefined>(branchId);

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const run = (async () => {
    const id = branchRef.current;
    if (!id) {
      setKpiSummary(null);
      setDaySummary(null);
      setLoading(false);
      return;
    }
    const gen = ++generationRef.current;
    setLoading(true);
    try {
      const [kpis, day] = await Promise.all([
        getCustomerKpiSummary(id),
        getCustomerDaySummary(id),
      ]);
      if (gen !== generationRef.current || !mountedRef.current) return;
      setKpiSummary(kpis);
      setDaySummary(day);
    } catch (error) {
      console.warn('[CustomerKpis] No se pudieron cargar los KPIs', error);
      if (gen === generationRef.current && mountedRef.current) {
        setKpiSummary(EMPTY_KPI);
        setDaySummary(EMPTY_DAY);
      }
    } finally {
      if (gen === generationRef.current && mountedRef.current) {
        setLoading(false);
      }
    }
    })();
    refreshInFlightRef.current = run;
    try {
      await run;
    } finally {
      refreshInFlightRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    branchRef.current = branchId;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [branchId, refresh]);

  useEffect(() => {
    const unsubscribe = subscribeCustomerSynced(() => {
      void refresh();
    });
    return unsubscribe;
  }, [refresh]);

  return {
    kpiSummary,
    daySummary,
    loading,
    refresh,
  };
}
